// Bestätigte Aktionen: Probelauf (plan) → Vorschau-Karte im ConfirmDialog → Ausführung mit dem effectHash der
// Vorschau. Hat sich die Wirkung bis zur Bestätigung geändert, antwortet der Server mit CONFLICT und der neuen
// Vorschau – der Dialog zeigt sie und lässt erneut bestätigen (docs/API.md §4, design-system §6.2).
//
//   const post = useConfirmedAction(receiptPost, { successMessage: 'Wareneingang gebucht' })
//   <Button variant="primary" loading={post.isPending} onClick={() => void post.run({ id })}>Wareneingang buchen</Button>
//
// Bei risk 'destructive' und 'financial' kommt die Vorschau von selbst; 'write' läuft direkt (confirm: 'always'
// erzwingt sie). Der Dialog steht einmal in der Anwendung (<ActionConfirmHost />, Teil von <ApiRuntime />).
import { TriangleAlert } from 'lucide-react'
import { useCallback, useState, useSyncExternalStore, type ReactNode } from 'react'
import type { AnyActionContract, InputArg, OutputOf } from '@shared/contract'
import { AppError, toAppError } from '@shared/errors'
import { ConfirmDialog } from '../ui/ConfirmDialog'
import { DiffValue, Money } from '../ui/format'
import { useApiClient } from './context'
import { toastError } from './errors'
import { useActionMutation, type ActionMutationOptions } from './hooks'
import type { ActionPlan, ActionPreview } from './types'

export const CONFLICT_HINT =
  'Die Lage hat sich inzwischen geändert. Bitte prüfen Sie die neue Vorschau und bestätigen Sie erneut.'

export const needsConfirmation = (contract: AnyActionContract): boolean =>
  contract.risk === 'destructive' || contract.risk === 'financial'

/** Neue Vorschau aus einer CONFLICT-Antwort (error.details.plan) – oder null. */
export function planFromConflict(error: AppError): ActionPlan | null {
  if (error.code !== 'CONFLICT') return null
  const plan = error.details?.plan as Partial<ActionPlan> | undefined
  return plan && typeof plan === 'object' && typeof plan.effectHash === 'string' && plan.preview
    ? (plan as ActionPlan)
    : null
}

// ---- der eine offene Dialog ---------------------------------------------------------------------------------

interface PendingConfirmation {
  /** gleich über alle Fassungen derselben Anfrage (eine neue Vorschau ersetzt nur `plan`) */
  id: number
  contract: AnyActionContract
  plan: ActionPlan
  confirmLabel: string
  /** führt mit dem Hash der gezeigten Vorschau aus; wirft bei Fehlern */
  execute: (plan: ActionPlan) => Promise<void>
  cancel: () => void
}

let pending: PendingConfirmation | null = null
let counter = 0
const listeners = new Set<() => void>()
const emit = (): void => listeners.forEach((listener) => listener())
const setPending = (next: PendingConfirmation | null): void => {
  pending = next
  emit()
}

/** Offene Bestätigung abbrechen (Abmelden, Tests). */
export function cancelPendingConfirmation(): void {
  pending?.cancel()
}

/** Zeilen der Vorschau-Karte: Vorher/Nachher, Umfang, Wert, Rückgängig. */
export function previewImpact(plan: ActionPlan): { label: string; value: ReactNode }[] {
  const { preview, totals } = plan
  const rows: { label: string; value: ReactNode }[] = (preview.diff ?? []).map((entry) => ({
    label: entry.label,
    value: (
      <DiffValue
        before={entry.before ?? undefined}
        after={entry.after ?? '–'}
        layout="arrow"
        className="flex-wrap justify-end whitespace-normal"
      />
    ),
  }))
  if (totals.itemCount > 1) rows.push({ label: 'Betroffene Datensätze', value: totals.itemCount })
  if (totals.valueCents !== undefined) rows.push({ label: 'Wert', value: <Money cents={totals.valueCents} /> })
  rows.push({
    label: 'Rückgängig',
    value: preview.undo.possible ? (preview.undo.label ?? 'möglich') : 'nicht möglich',
  })
  return rows
}

function PreviewText({ preview }: { preview: ActionPreview }) {
  // steht in der Beschreibung des Dialogs (ein <p>) – deshalb nur <span>, als Blöcke gesetzt
  return (
    <>
      <span className="block">{preview.summary}</span>
      {preview.warnings.map((warning) => (
        <span
          key={warning.code}
          className={
            warning.severity === 'warning'
              ? 'mt-2 flex items-start gap-2 text-dense text-warning-fg'
              : 'mt-2 flex items-start gap-2 text-dense text-fg-muted'
          }
        >
          {warning.severity === 'warning' && (
            <TriangleAlert className="mt-0.5 size-4 shrink-0" strokeWidth={1.75} aria-hidden />
          )}
          <span>{warning.message}</span>
        </span>
      ))}
    </>
  )
}

/** Der Bestätigungsdialog der API-Schicht. Einmal in der Anwendung, innerhalb von <UiProvider>. */
export function ActionConfirmHost() {
  const current = useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => pending,
    () => null,
  )
  // der Dialog blendet aus, nachdem die Anfrage erledigt ist – so lange bleibt der letzte Inhalt stehen
  const [shown, setShown] = useState<PendingConfirmation | null>(null)
  if (current && current !== shown) setShown(current)
  if (!shown) return null
  const open = current !== null
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && pending) pending.cancel()
      }}
      title={shown.plan.preview.title}
      description={<PreviewText preview={shown.plan.preview} />}
      impact={previewImpact(shown.plan)}
      tone={shown.contract.risk === 'destructive' ? 'danger' : 'neutral'}
      confirmLabel={shown.confirmLabel}
      onConfirm={() => shown.execute(shown.plan)}
    />
  )
}

// ---- Hook ---------------------------------------------------------------------------------------------------

export interface ConfirmedActionOptions<C extends AnyActionContract> extends ActionMutationOptions<C> {
  /** 'auto' (Standard): Vorschau bei risk 'destructive' und 'financial' · 'always' · 'never' */
  confirm?: 'auto' | 'always' | 'never'
  /** Beschriftung der Bestätigung – wiederholt das Verb („Buchen“, „Stornieren“). Standard: Titel der Aktion */
  confirmLabel?: string
}

export interface ConfirmedAction<C extends AnyActionContract> {
  /** Vorschau zeigen, bestätigen lassen, ausführen. Liefert die Ausgabe – oder undefined nach Abbruch bzw. Fehler (Toast). */
  run: (input: InputArg<C>) => Promise<OutputOf<C> | undefined>
  /** wie run(), lehnt aber bei Fehlern mit AppError ab (Formulare hängen ihn an die Felder); undefined = abgebrochen */
  runAsync: (input: InputArg<C>) => Promise<OutputOf<C> | undefined>
  /** Vorschau wird geholt oder die Aktion läuft */
  isPending: boolean
  error: AppError | null
}

export function useConfirmedAction<C extends AnyActionContract>(
  contract: C,
  options: ConfirmedActionOptions<C> = {},
): ConfirmedAction<C> {
  const client = useApiClient()
  const { confirm = 'auto', confirmLabel, ...mutationOptions } = options
  // Fehler beim Ausführen zeigt der Dialog selbst (er bleibt offen) – kein zweiter Toast
  const viaDialog = useActionMutation(contract, { ...mutationOptions, errorToast: false })
  const direct = useActionMutation(contract, mutationOptions)
  const [planning, setPlanning] = useState(false)
  const [error, setError] = useState<AppError | null>(null)
  const withPreview = confirm === 'always' || (confirm === 'auto' && needsConfirmation(contract))
  const { mutateAsync: executeConfirmed } = viaDialog
  const { mutateAsync: executeDirect } = direct

  const runAsync = useCallback(
    async (input: InputArg<C>): Promise<OutputOf<C> | undefined> => {
      setError(null)
      if (!withPreview) return executeDirect(input)
      let plan: ActionPlan
      setPlanning(true)
      try {
        plan = await client.plan(contract, input)
      } catch (cause) {
        const failed = toAppError(cause)
        setError(failed)
        throw failed
      } finally {
        setPlanning(false)
      }
      return new Promise<OutputOf<C> | undefined>((resolve) => {
        const id = ++counter
        const close = (): void => {
          if (pending?.id === id) setPending(null)
        }
        const request: PendingConfirmation = {
          id,
          contract,
          plan,
          confirmLabel: confirmLabel ?? contract.title,
          cancel: () => {
            close()
            resolve(undefined)
          },
          execute: async (shownPlan) => {
            try {
              const output = await executeConfirmed(input, { expectedEffectHash: shownPlan.effectHash })
              close()
              resolve(output)
            } catch (cause) {
              const failed = toAppError(cause)
              const fresh = planFromConflict(failed)
              if (!fresh) throw failed
              // neue Vorschau zeigen; der Dialog bleibt offen und nennt den Grund
              if (pending?.id === id) setPending({ ...pending, plan: fresh })
              throw new AppError('CONFLICT', CONFLICT_HINT)
            }
          },
        }
        // nie zwei Dialoge übereinander: eine neue Anfrage beantwortet die offene mit „abgebrochen“
        pending?.cancel()
        setPending(request)
      })
    },
    [client, contract, confirmLabel, executeConfirmed, executeDirect, withPreview],
  )

  const run = useCallback(
    async (input: InputArg<C>): Promise<OutputOf<C> | undefined> => {
      try {
        return await runAsync(input)
      } catch (cause) {
        // direkter Weg: Der Toast kam schon aus useActionMutation. Hier bleibt nur der gescheiterte Probelauf.
        if (withPreview) toastError(cause, { contract })
        return undefined
      }
    },
    [contract, runAsync, withPreview],
  )

  return {
    run,
    runAsync,
    isPending: planning || viaDialog.isPending || direct.isPending,
    error: error ?? viaDialog.error ?? direct.error,
  }
}
